import { IonButton, IonCol, IonContent, IonIcon, IonInput, IonItem, IonLabel, IonNote, IonRow, IonSpinner, useIonToast, useIonViewDidEnter } from '@ionic/react';
import { cloudUpload, key, person, phonePortrait, power } from 'ionicons/icons';
import Header from '../components/Header/Header';
import Footer from '../components/Footer';
import { onScroll } from '../services/sticky-menu.service';
import { useAppContext, useAppContextUpdater } from '../services/app-context.service';
import { useHistory } from 'react-router-dom';
import { useState } from 'react';
import User from '../types/user.type';
import loginService from '../services/loginService';
import './ProfileEdit.scss';

const Profile: React.FC = () => {
  const data = useAppContext() as any
  const setAppData = useAppContextUpdater()
  const [user, setUser] = useState(data.user as User)
  const [name, setName] = useState(data.user.name ?? '')
  const [phone, setPhone] = useState(data.user.phone ?? '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [nameError, setNameError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [confirmPassError, setConfirmPassError] = useState('')
  const [passError, setPassError] = useState('')
  const [buttonLoading, setButtonLoading] = useState(false)
  const history = useHistory()
  const [presentToast] = useIonToast();

  useIonViewDidEnter(() => {
    const tabBar = document.querySelector('ion-tab-bar');
    if (tabBar !== null) {
      tabBar.style.display = 'flex';
    }
  })

  function afterLogout() {
    history.push('/')
  }

  function logout() {
    if (data.user.token) {
      loginService.doLogOut()
        .catch(error => console.error('error', error))
        .finally(() => {
          setAppData({ ...data, user: loginService.currentUser })
          afterLogout()
        })
    } else {
      afterLogout()
    }
  }

  function resetErrors() {
    setNameError('')
    setPhoneError('')
    setPassError('')
    setConfirmPassError('')
  }

  function onChange() {
    resetErrors()
  }

  async function onSubmit() {
    resetErrors()

    let hasError = false
    if (!name.trim()) {
      setNameError('Name is required')
      hasError = true
    }
    if (!phone.trim()) {
      setPhoneError('Phone is required')
      hasError = true
    }
    if (password && password.length < 6) {
      setPassError('Password must be at least 6 characters')
      hasError = true
    }
    if (password !== confirmPassword) {
      setConfirmPassError('Passwords do not match')
      hasError = true
    }

    if (hasError) {
      return
    }

    setButtonLoading(true)
    try {
      await loginService.editDetails({
        name: name.trim(),
        phone: phone.trim(),
        password: password || undefined,
        password_confirmation: confirmPassword || undefined,
      })

      const updatedUser = loginService.currentUser
      setUser(updatedUser)
      setName(updatedUser.name ?? '')
      setPhone(updatedUser.phone ?? '')
      setPassword('')
      setConfirmPassword('')
      setAppData({ ...data, user: updatedUser })
      presentToast({
        message: 'Profile updated successfully',
        duration: 2000,
        color: 'success',
      })
    } catch (error: any) {
      const messages = error?.response?.data?.message
      if (messages?.name?.[0]) {
        setNameError(messages.name[0])
      }
      if (messages?.phone?.[0]) {
        setPhoneError(messages.phone[0])
      }
      if (messages?.password?.[0]) {
        setPassError(messages.password[0])
      }
      presentToast({
        message: 'Failed to update profile',
        duration: 2500,
        color: 'danger',
      })
    } finally {
      setButtonLoading(false)
    }
  }

  return (
    <IonContent fullscreen scrollEvents={true} onIonScroll={onScroll}>
      <Header />
      <IonRow>
        <IonCol sizeXl='8' offsetXl='2' sizeLg='10' offsetLg='1' className='no-padding bg-content'>
          <div className='page-container profile-page'>
            <div className='profile-page-shell'>
              <div className='title-bar profile-page-header'>
                <div>
                  <h1>My Profile</h1>
                  <p className='profile-page-subtitle'>Update your account name, phone number, and password.</p>
                </div>
                <IonButton onClick={logout} fill='outline' color='medium' className='profile-logout-button'>
                  <IonIcon icon={power} slot='start' />
                  Logout
                </IonButton>
              </div>

              <div className='profile-form-card'>
              <IonItem className={`profile-field ${!!nameError && 'ion-invalid'}`}>
                <IonLabel position="floating"><IonIcon icon={person} />Name</IonLabel>
                <IonInput clearInput={true} onIonChange={(e) => {
                  onChange()
                  setName(e.detail.value ?? '')
                }} value={name} />
                {!!nameError && <IonNote slot='error'>{nameError}</IonNote>}
              </IonItem>
              <IonItem className={`profile-field ${!!phoneError && 'ion-invalid'}`}>
                <IonLabel position="floating"><IonIcon icon={phonePortrait} />Mobile</IonLabel>
                <IonInput clearInput={true} onIonChange={(e) => {
                  onChange()
                  setPhone(e.detail.value ?? '')
                }} value={phone} />
                {!!phoneError && <IonNote slot='error'>{phoneError}</IonNote>}
              </IonItem>
              <IonItem className={`profile-field ${!!passError && 'ion-invalid'}`}>
                <IonLabel position="floating"><IonIcon icon={key} />Password</IonLabel>
                <IonInput type='password' clearInput={true} onIonChange={(e) => {
                  onChange()
                  setPassword(e.detail.value ?? '')
                }} value={password} />
                {!!passError && <IonNote slot='error'>{passError}</IonNote>}
              </IonItem>
              <IonItem className={`profile-field ${!!confirmPassError && 'ion-invalid'}`}>
                <IonLabel position="floating"><IonIcon icon={key} />Confirm Password</IonLabel>
                <IonInput type='password' clearInput={true} onIonChange={(e) => {
                  onChange()
                  setConfirmPassword(e.detail.value ?? '')
                }} value={confirmPassword} />
                {!!confirmPassError && <IonNote slot='error'>{confirmPassError}</IonNote>}
              </IonItem>

              <div className='profile-actions'>
                <IonButton disabled={buttonLoading} onClick={onSubmit} expand='block' color="tertiary" className='profile-save-button'>
                  {!buttonLoading && <IonIcon icon={cloudUpload} slot="start"/>}
                  {buttonLoading && <IonSpinner slot="start" name="lines-sharp"></IonSpinner>}
                  {buttonLoading ? "Saving Changes" : "Save Changes"}
                </IonButton>
              </div>
              </div>
            </div>
          </div>
        </IonCol>
      </IonRow>
      <Footer />
    </IonContent>
  );
};

export default Profile;
